import type { RouteRecordRaw } from 'vue-router'

import { CALCULATOR_ENABLED } from '@/config/features'
import type { AccessLevel } from '@/types/auth.types'

/**
 * Library owns the route table; pms-fe fills the view bodies. New
 * routes (workflow, action-*, wizard, payment-detail) appended in
 * feature `feat/PMS-agenda-UX`.
 *
 * Conventions:
 *   • path prefix: `/pms/...`
 *   • meta.access_levels gates the route to admin + manager only.
 *     `App.vue`'s shell selector picks `PmsShell` when the path
 *     starts with `/pms` (wired by the library agent in phase 3).
 *   • lazy-loaded components: `() => import('@/views/pms/AgendaView.vue')`
 *   • static-vs-dynamic ordering: the static `/pms/partners/new`
 *     wizard route MUST be declared BEFORE the dynamic
 *     `/pms/partners/:id` profile route — vue-router 4 takes the
 *     first matching record and a `:id` placeholder would otherwise
 *     swallow the literal `new` segment.
 *   • catch-all is library-owned in `router/index.ts`; this file
 *     never declares a `:pathMatch(.*)*` entry.
 */
const STAFF: AccessLevel[] = ['access_level.admin', 'access_level.manager']

/** Partner-profile query that opens «Studio AI» and runs the study once on arrival. */
export const PROFILE_RUN_STUDY_QUERY = { tab: 'ai', studio: 'genera' } as const

// Shell selector chooses PmsShell when path starts with /pms — see App.vue.
export const pmsRoutes: RouteRecordRaw[] = [
  { path: '/pms', redirect: '/pms/dashboard' },
  {
    path: '/pms/dashboard',
    name: 'pms-dashboard',
    component: () => import('@/views/pms/DashboardView.vue'),
    // Admins and leaders only — enforced by the global guard in `router/index.ts`.
    meta: { access_levels: STAFF, leader_only: true },
  },
  {
    path: '/pms/agenda',
    name: 'pms-agenda',
    component: () => import('@/views/pms/AgendaView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/partners',
    name: 'pms-partners',
    component: () => import('@/views/pms/DirectoryView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    // Wizard route MUST appear before `/pms/partners/:id` — vue-router 4
    // matches the first record, and `:id` would otherwise swallow `new`.
    path: '/pms/partners/new',
    name: 'pms-partner-new',
    component: () => import('@/views/pms/AddPartnerWizardView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    // §4.8 many-at-once potentials intake. Same static-before-dynamic
    // ordering requirement as `/pms/partners/new` above.
    path: '/pms/partners/new-bulk',
    name: 'pms-partner-new-bulk',
    component: () => import('@/views/pms/AddPartnersBulkView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/partners/:id',
    name: 'pms-partner-detail',
    component: () => import('@/views/pms/PartnerProfileView.vue'),
    meta: { access_levels: STAFF },
  },
  // ─── Agencies (Phase-2E PMS design alignment) ──────────────────────
  // Static `/new` MUST precede dynamic `/:id` — same rule that protects
  // the partners wizard against the `:id` placeholder swallowing `new`.
  {
    path: '/pms/agencies',
    name: 'pms-agencies',
    component: () => import('@/views/pms/agencies/AgenciesDirectoryView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/agencies/new',
    name: 'pms-agency-new',
    component: () => import('@/views/pms/agencies/AddAgencyWizardView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/agencies/:id',
    name: 'pms-agency-detail',
    component: () => import('@/views/pms/agencies/AgencyDetailView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/payments',
    name: 'pms-payments',
    component: () => import('@/views/pms/PaymentsView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/coupons',
    name: 'pms-coupons',
    component: () => import('@/views/pms/CouponArchiveView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/coupons/new',
    name: 'pms-coupon-new',
    component: () => import('@/views/pms/CouponCreatorView.vue'),
    meta: { access_levels: STAFF },
  },
  // ─── Agenda Import (legacy monthly Agenda .xlsx intake) ────────────
  // Static list route precedes the dynamic `:id` detail — vue-router 4
  // matches the first record, and `:id` would otherwise be ambiguous.
  {
    path: '/pms/agenda-import',
    name: 'pms-agenda-import',
    component: () => import('@/views/pms/AgendaImportListView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/agenda-import/:id',
    name: 'pms-agenda-import-detail',
    component: () => import('@/views/pms/AgendaImportReconcileView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/closure',
    name: 'pms-closure',
    component: () => import('@/views/pms/ClosureView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/report',
    name: 'pms-report',
    component: () => import('@/views/pms/ChannelReportView.vue'),
    meta: { access_levels: STAFF },
  },
  // The calculator is out of service: a deep link lands on the agenda instead.
  CALCULATOR_ENABLED
    ? {
        path: '/pms/calculator',
        name: 'pms-calculator',
        component: () => import('@/views/pms/CalculatorView.vue'),
        meta: { access_levels: STAFF },
      }
    : {
        path: '/pms/calculator',
        name: 'pms-calculator',
        redirect: '/pms/agenda',
        meta: { access_levels: STAFF },
      },
  {
    path: '/pms/settings',
    name: 'pms-settings',
    component: () => import('@/views/pms/SettingsView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/drive',
    name: 'pms-drive',
    component: () => import('@/views/pms/DriveView.vue'),
    meta: { access_levels: STAFF },
  },
  // ─── PMS Agenda UX (feat/PMS-agenda-UX) ────────────────────────────
  // Per-row state-machine workflow + per-step action pages.
  {
    path: '/pms/agenda/:rowId',
    name: 'pms-agenda-workflow',
    component: () => import('@/views/pms/AgendaWorkflowView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/agenda/:rowId/call',
    name: 'pms-agenda-action-call',
    component: () => import('@/views/pms/ActionCallView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/agenda/:rowId/coupon',
    name: 'pms-agenda-action-coupon',
    component: () => import('@/views/pms/ActionCouponView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/agenda/:rowId/script',
    name: 'pms-agenda-action-script',
    component: () => import('@/views/pms/ActionScriptView.vue'),
    meta: { access_levels: STAFF },
  },
  {
    path: '/pms/agenda/:rowId/launch',
    name: 'pms-agenda-action-launch',
    component: () => import('@/views/pms/ActionLaunchView.vue'),
    meta: { access_levels: STAFF },
  },
  // Per-invoice payment detail timeline.
  {
    path: '/pms/payments/:invoiceId',
    name: 'pms-payment-detail',
    component: () => import('@/views/pms/PaymentDetailView.vue'),
    meta: { access_levels: STAFF },
  },
]
